import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, MapPin, Plane, Phone, Mail, MessageCircle, Send, CheckCircle, User } from 'lucide-react';
import { useCustomTours } from '../../hooks/useCustomTours';
import CustomDropdown from '../ui/CustomDropdown';
import { appConfig } from '../../config/appConfig';
import { submitCustomTourInquiry } from '../../services/customTourInquiryService';

interface CustomTourFormData {
  destination: string;
  isGroup: boolean;
  numberOfPeople: number;
  departureDate: string;
  arrivalDate: string;
  name: string;
  email: string;
  phoneNumber: string;
}

interface CustomTourFormProps {
  onSubmit?: (data: CustomTourFormData) => void;
}

const countryCodes = [
  { code: '+91', country: 'India' },
  { code: '+1', country: 'US/Canada' },
  { code: '+44', country: 'UK' },
  { code: '+971', country: 'UAE' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+65', country: 'Singapore' },
  { code: '+60', country: 'Malaysia' },
  { code: '+66', country: 'Thailand' },
  { code: '+62', country: 'Indonesia' },
  { code: '+81', country: 'Japan' },
  { code: '+82', country: 'South Korea' },
  { code: '+86', country: 'China' },
  { code: '+61', country: 'Australia' },
  { code: '+64', country: 'New Zealand' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+39', country: 'Italy' },
  { code: '+34', country: 'Spain' },
  { code: '+974', country: 'Qatar' },
  { code: '+965', country: 'Kuwait' },
  { code: '+973', country: 'Bahrain' },
  { code: '+968', country: 'Oman' },
  { code: '+94', country: 'Sri Lanka' },
  { code: '+977', country: 'Nepal' },
  { code: '+880', country: 'Bangladesh' },
];

const CustomTourForm: React.FC<CustomTourFormProps> = ({ onSubmit }) => {
  const { customTours, loading, error } = useCustomTours();
  const [formData, setFormData] = useState<CustomTourFormData>({
    destination: '',
    isGroup: false,
    numberOfPeople: 1,
    departureDate: '',
    arrivalDate: '',
    name: '',
    email: '',
    phoneNumber: '',
  });
  const [countryCode, setCountryCode] = useState('+91');
  const [selectedCountryMonths, setSelectedCountryMonths] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const countryOptions = React.useMemo(() => {
    const countries = new Set<string>();
    customTours.forEach(tour => {
      if (tour.country) {
        countries.add(tour.country);
      }
    });
    return Array.from(countries).sort().map(country => ({
      value: country,
      label: country
    }));
  }, [customTours]);

  useEffect(() => {
    if (formData.destination) {
      const countryTour = customTours.find(tour => tour.country === formData.destination);
      setSelectedCountryMonths(countryTour?.months || []);
    } else {
      setSelectedCountryMonths([]);
    }
  }, [formData.destination, customTours]);

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const handleInputChange = (field: keyof CustomTourFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSubmitError(null);

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.destination) {
      newErrors.destination = 'Please select a destination';
    }

    if (formData.numberOfPeople < 1) {
      newErrors.numberOfPeople = 'Number of people must be at least 1';
    }

    if (!formData.departureDate) {
      newErrors.departureDate = 'Please select a departure date';
    }

    if (!formData.arrivalDate) {
      newErrors.arrivalDate = 'Please select an arrival date';
    }

    if (formData.departureDate && formData.arrivalDate) {
      const departure = new Date(formData.departureDate);
      const arrival = new Date(formData.arrivalDate);

      if (departure >= arrival) {
        newErrors.arrivalDate = 'Arrival date must be after departure date';
      }
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }

    const digitsOnly = formData.phoneNumber.replace(/\D/g, '');
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (digitsOnly.length < 7) {
      newErrors.phoneNumber = 'Phone number must be at least 7 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    const selectedCustomTour = customTours.find(tour => tour.country === formData.destination);
    if (!selectedCustomTour) {
      setSubmitError('Selected destination not found in available tours.');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitCustomTourInquiry({
        customTourId: selectedCustomTour.id,
        country: formData.destination,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phoneNumber: `${countryCode}${formData.phoneNumber.replace(/\s/g, '')}`,
        isGroup: formData.isGroup,
        numberOfPeople: formData.numberOfPeople,
        departureDate: formData.departureDate,
        arrivalDate: formData.arrivalDate,
      });

      setSubmitted(true);
      if (onSubmit) {
        onSubmit(formData);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit your interest. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      destination: '',
      isGroup: false,
      numberOfPeople: 1,
      departureDate: '',
      arrivalDate: '',
      name: '',
      email: '',
      phoneNumber: '',
    });
    setCountryCode('+91');
    setErrors({});
    setSubmitError(null);
    setSubmitted(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-secondary-slate">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-deep-blue"></div>
          <span className="body-large">Loading destinations...</span>
        </div>
      </div>
    );
  }

  if (error || customTours.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
        <h4 className="heading-tertiary text-neutral-charcoal mb-3">
          Custom plans are not available right now
        </h4>
        <p className="body-large text-secondary-slate mb-6 max-w-xl mx-auto">
          Contact us and our team will help plan a journey tailored to you.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={appConfig.social.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-whatsapp"
          >
            <MessageCircle size={20} />
            WhatsApp Us
          </a>
          <a
            href={`tel:${appConfig.contact.phone.replace(/\s/g, '')}`}
            className="btn btn-primary"
          >
            <Phone size={20} />
            {appConfig.contact.phone}
          </a>
          <a
            href={`mailto:${appConfig.contact.email}`}
            className="btn btn-secondary"
          >
            <Mail size={20} />
            Email Us
          </a>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl p-8 text-center"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 text-[var(--success)] mb-4">
          <CheckCircle size={32} />
        </div>
        <h4 className="heading-tertiary text-neutral-charcoal mb-3">
          We received your interest
        </h4>
        <p className="body-large text-secondary-slate mb-6 max-w-xl mx-auto">
          Our team will contact you about a custom journey to {formData.destination} using {formData.email} and {countryCode}{formData.phoneNumber.replace(/\s/g, '')}.
        </p>
        <button
          type="button"
          onClick={handleReset}
          className="btn btn-secondary"
        >
          Request another destination
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-2xl shadow-xl p-8"
    >
      <form onSubmit={handleSubmit} className="space-y-8">
        <section className="space-y-6">
          <h3 className="text-lg font-semibold text-neutral-charcoal">
            Tour details
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-semibold text-neutral-charcoal">
                <MapPin size={16} className="text-primary-deep-blue" />
                Destination
              </label>
              <CustomDropdown
                value={formData.destination}
                onChange={(value) => handleInputChange('destination', value)}
                options={countryOptions}
                placeholder="Select a destination"
                error={!!errors.destination}
              />
              {errors.destination && (
                <p className="text-sm text-error">{errors.destination}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-semibold text-neutral-charcoal">
                <Users size={16} className="text-primary-deep-blue" />
                Number of People
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={formData.numberOfPeople}
                onChange={(e) => handleInputChange('numberOfPeople', parseInt(e.target.value) || 1)}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-deep-blue focus:border-transparent transition-all duration-200 ${
                  errors.numberOfPeople ? 'border-error' : 'border-neutral-light-gray'
                }`}
              />
              {errors.numberOfPeople && (
                <p className="text-sm text-error">{errors.numberOfPeople}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isGroup}
                onChange={(e) => handleInputChange('isGroup', e.target.checked)}
                className="w-5 h-5 text-primary-deep-blue border-2 border-neutral-light-gray rounded focus:ring-primary-deep-blue"
              />
              <div className="flex items-center gap-2">
                <Users size={16} className="text-primary-deep-blue" />
                <span className="text-base font-semibold text-neutral-charcoal">
                  Group Travel
                </span>
              </div>
            </label>
            {formData.isGroup && (
              <p className="text-sm text-secondary-slate">
                By selecting this, you are expressing interest in traveling with others outside your own group.
              </p>
            )}
            {formData.isGroup && selectedCountryMonths.length > 0 && (
              <div className="p-3 bg-primary-deep-blue bg-opacity-5 rounded-lg">
                <p className="text-sm text-primary-deep-blue font-medium mb-2">
                  Available months for {formData.destination}:
                </p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {selectedCountryMonths.map(month => (
                    <span
                      key={month}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary-deep-blue text-white text-xs rounded-full"
                    >
                      {month}
                    </span>
                  ))}
                </div>
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-xs text-amber-800 font-medium">
                    Please select departure and arrival dates only within the available months shown above for group travel.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-semibold text-neutral-charcoal">
                <Plane size={16} className="text-primary-deep-blue" />
                Departure Date
              </label>
              <input
                type="date"
                value={formData.departureDate}
                onChange={(e) => handleInputChange('departureDate', e.target.value)}
                min={formatDateForInput(new Date())}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-deep-blue focus:border-transparent transition-all duration-200 ${
                  errors.departureDate ? 'border-error' : 'border-neutral-light-gray'
                }`}
              />
              {errors.departureDate && (
                <p className="text-sm text-error">{errors.departureDate}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-semibold text-neutral-charcoal">
                <Calendar size={16} className="text-primary-deep-blue" />
                Arrival Date
              </label>
              <input
                type="date"
                value={formData.arrivalDate}
                onChange={(e) => handleInputChange('arrivalDate', e.target.value)}
                min={formData.departureDate || formatDateForInput(new Date())}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-deep-blue focus:border-transparent transition-all duration-200 ${
                  errors.arrivalDate ? 'border-error' : 'border-neutral-light-gray'
                }`}
              />
              {errors.arrivalDate && (
                <p className="text-sm text-error">{errors.arrivalDate}</p>
              )}
            </div>
          </div>
        </section>

        <div className="border-t border-neutral-light-gray" />

        <section className="space-y-6">
          <h3 className="text-lg font-semibold text-neutral-charcoal">
            Your details
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-semibold text-neutral-charcoal">
                <User size={16} className="text-primary-deep-blue" />
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Your name"
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-deep-blue focus:border-transparent transition-all duration-200 ${
                  errors.name ? 'border-error' : 'border-neutral-light-gray'
                }`}
              />
              {errors.name && (
                <p className="text-sm text-error">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-semibold text-neutral-charcoal">
                <Mail size={16} className="text-primary-deep-blue" />
                Email Address
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="you@example.com"
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-deep-blue focus:border-transparent transition-all duration-200 ${
                  errors.email ? 'border-error' : 'border-neutral-light-gray'
                }`}
              />
              {errors.email && (
                <p className="text-sm text-error">{errors.email}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-base font-semibold text-neutral-charcoal">
              <Phone size={16} className="text-primary-deep-blue" />
              Phone Number
            </label>
            <div className="flex gap-2">
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="px-3 py-3 border border-neutral-light-gray rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-deep-blue bg-white min-w-[120px]"
              >
                {countryCodes.map((country, index) => (
                  <option key={`${country.code}-${country.country}-${index}`} value={country.code}>
                    {country.code} {country.country}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                placeholder="Enter your phone number"
                className={`flex-1 px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-deep-blue focus:border-transparent transition-all duration-200 ${
                  errors.phoneNumber ? 'border-error' : 'border-neutral-light-gray'
                }`}
              />
            </div>
            {errors.phoneNumber && (
              <p className="text-sm text-error">{errors.phoneNumber}</p>
            )}
          </div>
        </section>

        {submitError && (
          <p className="text-sm text-error">{submitError}</p>
        )}

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn btn-luxury flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Send size={20} />
            {isSubmitting ? 'Sending request...' : 'Request a Custom Tour'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default CustomTourForm;
